import { useCallback, useState } from "react";

// feat_396 — estado del filtro de colas ("Departamento" + "Todos" + "Sin
// asignar") extraído de TicketsManager para que lo use también el Funnel.
//
// El hook NO persiste: cada pantalla guarda el snapshot en SU propia clave de
// localStorage (bandeja `waia:ticketsFilter:…`, Funnel `waia:crmBoardFilter:…`)
// junto con el resto de sus filtros. Así destildar una cola en una pantalla no
// toca la otra.
//
// `persisted` = lo que devolvió parseStoredQueueFilter, o null en la primera
// visita → default = lo que el usuario ve en Chats: todas sus colas, "Sin
// asignar" tildado, y "Todos" prendido sólo para admin.

// Valida los campos de colas de un objeto guardado contra las colas reales del
// usuario (una cola que ya no tiene se descarta). Mismo criterio de siempre de
// la bandeja.
export const parseStoredQueueFilter = (parsed, userQueues) => {
  const validIds = (userQueues || []).map(q => q.id);
  const filteredQueues = (parsed.selectedQueueIds || []).filter(id => validIds.includes(id));
  const filteredPrev = parsed.prevSelectedQueueIds
    ? parsed.prevSelectedQueueIds.filter(id => validIds.includes(id))
    : null;
  return {
    selectedQueueIds: filteredQueues,
    showAllTickets: !!parsed.showAllTickets,
    showOrphans: parsed.showOrphans !== false,
    prevShowOrphans: parsed.prevShowOrphans ?? null,
    prevSelectedQueueIds: filteredPrev,
  };
};

const useQueueFilterState = ({ user, persisted }) => {
  const userQueueIds = (user.queues || []).map(q => q.id);

  // Con estado persistido se respeta tal cual (aunque sea false); sin él,
  // "Todos" arranca ON solo para admin (primera visita).
  const [showAllTickets, setShowAllTickets] = useState(
    persisted
      ? !!persisted.showAllTickets
      : user.profile?.toUpperCase() === "ADMIN"
  );
  const [selectedQueueIds, setSelectedQueueIds] = useState(
    persisted?.selectedQueueIds ?? userQueueIds
  );
  const [showOrphans, setShowOrphans] = useState(
    persisted?.showOrphans ?? true
  );
  // prevShowOrphans / prevSelectedQueueIds store values before "Todos" ON to restore on toggle-off
  const [prevShowOrphans, setPrevShowOrphans] = useState(
    persisted?.prevShowOrphans ?? null
  );
  const [prevSelectedQueueIds, setPrevSelectedQueueIds] = useState(
    persisted?.prevSelectedQueueIds ?? null
  );

  const handleToggleShowAll = () => {
    setShowAllTickets(prev => {
      const next = !prev;
      if (next) {
        // Turning Todos ON: memorize current state and show everything
        setPrevShowOrphans(showOrphans);
        setPrevSelectedQueueIds(selectedQueueIds);
        setShowOrphans(true);
        setSelectedQueueIds((user.queues || []).map(q => q.id));
      } else {
        // Turning Todos OFF: restore previous state
        if (prevShowOrphans !== null) {
          setShowOrphans(prevShowOrphans);
          setPrevShowOrphans(null);
        }
        if (prevSelectedQueueIds !== null) {
          setSelectedQueueIds(prevSelectedQueueIds);
          setPrevSelectedQueueIds(null);
        }
      }
      return next;
    });
  };

  const toggleShowOrphans = useCallback(() => setShowOrphans(prev => !prev), []);

  // Vuelve al default de la primera visita (lo usa "Limpiar filtros" del Funnel).
  const resetQueueFilter = () => {
    setShowAllTickets(user.profile?.toUpperCase() === "ADMIN");
    setSelectedQueueIds((user.queues || []).map(q => q.id));
    setShowOrphans(true);
    setPrevShowOrphans(null);
    setPrevSelectedQueueIds(null);
  };

  return {
    showAllTickets,
    setShowAllTickets,
    selectedQueueIds,
    setSelectedQueueIds,
    showOrphans,
    setShowOrphans,
    prevShowOrphans,
    prevSelectedQueueIds,
    handleToggleShowAll,
    toggleShowOrphans,
    resetQueueFilter,
  };
};

export default useQueueFilterState;
